/* ═══════════════════════════════════════════════════════════════
   SpecService — Git-like branching model for Spec Masters

   Published SpecMaster = main branch (never edited directly)
   SpecMasterDraft      = feature branch (local working copy)
   Publish Request      = pull request (diff review by admin)

   Flow: checkout → edit draft → save draft → submit publish → admin review → merge
   ═══════════════════════════════════════════════════════════════ */

import type {
  CsiDivision,
  SystemCatalogEntry,
  SpecMaster,
  SpecMasterDraft,
  SpecMasterVersion,
  SpecMasterStatus,
  BlockDiff,
  ContentBlock,
  Project,
  AssembledSpec,
  SystemModule,
  Variant,
} from '../../shared/types';
import { pushEditHistory } from '../../shared/editHistory';
import { ProjectService } from './projectService';
import { SpecMasterService } from './specMasterService';
import * as cache from './cloudCache';
import { log as logActivity } from './activityLogger';
import { publishWrites, type PublishResult } from '../spec-studio/publishPlan';
import { VariantChangeService } from './variantChangeService';
import { buildInlineDiff } from '../../shared/block-diff';

export const CSI_DIVISIONS: CsiDivision[] = [
  { code: '01', name: 'General Requirements' },
  { code: '02', name: 'Existing Conditions' },
  { code: '03', name: 'Concrete' },
  { code: '04', name: 'Masonry' },
  { code: '05', name: 'Metals' },
  { code: '06', name: 'Wood, Plastics, and Composites' },
  { code: '07', name: 'Thermal and Moisture Protection' },
  { code: '08', name: 'Openings' },
  { code: '09', name: 'Finishes' },
  { code: '10', name: 'Specialties' },
  { code: '11', name: 'Equipment' },
  { code: '12', name: 'Furnishings' },
  { code: '13', name: 'Special Construction' },
  { code: '14', name: 'Conveying Equipment' },
  { code: '21', name: 'Fire Suppression' },
  { code: '22', name: 'Plumbing' },
  { code: '23', name: 'Heating, Ventilating, and Air Conditioning' },
  { code: '25', name: 'Integrated Automation' },
  { code: '26', name: 'Electrical' },
  { code: '27', name: 'Communications' },
  { code: '28', name: 'Electronic Safety and Security' },
  { code: '31', name: 'Earthwork' },
  { code: '32', name: 'Exterior Improvements' },
  { code: '33', name: 'Utilities' },
];

/* ─── System Catalog (reference data for system types) ─── */
export const SYSTEM_CATALOG: SystemCatalogEntry[] = [
  {
    id: 'fire-protection',
    name: 'Fire Protection',
    icon: '🔥',
    description: 'Fire-rated assemblies & suppression',
  },
  { id: 'seismic', name: 'Seismic', icon: '🌊', description: 'Seismic bracing & design criteria' },
  {
    id: 'sustainability',
    name: 'Sustainability',
    icon: '🌿',
    description: 'LEED, green requirements',
  },
  {
    id: 'moisture',
    name: 'Moisture Control',
    icon: '💧',
    description: 'Vapor barriers, waterproofing',
  },
  { id: 'acoustic', name: 'Acoustic', icon: '🔇', description: 'STC/IIC ratings, sound control' },
  { id: 'security', name: 'Security', icon: '🔒', description: 'Access control, blast-resistance' },
  {
    id: 'hvac-integration',
    name: 'HVAC Integration',
    icon: '🌡️',
    description: 'Ductwork, plenums, insulation coordination',
  },
  {
    id: 'code-compliance',
    name: 'Code Compliance',
    icon: '📋',
    description: 'IBC, ADA, local jurisdiction',
  },
];

/* ═══════════════════════════════════════════════════════════════
   WORD-LEVEL DIFF / INLINE DIFF — one brain, now shared.

   The implementations moved to `src/shared/block-diff.ts` (Phase 3) so the
   master → variant update feed can compute the SAME diff outside the
   renderer. Re-exported here unchanged: every existing importer
   (TrackChangesView, designer/issuance, designer/revisions, Studio) keeps
   its import path and its behaviour.
   ═══════════════════════════════════════════════════════════════ */

export { buildInlineDiff, diffWords } from '../../shared/block-diff';
export type { DiffToken, InlineDiffBlock, InlineDiffKind } from '../../shared/block-diff';

/* ═══════════════════════════════════════════════════════════════
   REVISION MARKS — attach computed revision info to a block list.
   Returns the new blocks in document order with `revision` stamped on
   added/modified blocks, and removed old blocks spliced back in at the
   positions they previously occupied (kind: 'removed'). Feed the result
   to SpecFormattedView or buildSpecDocx with RevisionSettings to render
   architect-style change marks. Purely computed — nothing is persisted.
   ═══════════════════════════════════════════════════════════════ */

export function applyRevisionMarks(
  oldBlocks: ContentBlock[],
  newBlocks: ContentBlock[]
): ContentBlock[] {
  const diff = buildInlineDiff(oldBlocks, newBlocks);
  const newById = new Map(newBlocks.map((b) => [b.id, b]));
  const oldById = new Map(oldBlocks.map((b) => [b.id, b]));
  const out: ContentBlock[] = [];
  for (const d of diff) {
    if (d.kind === 'removed') {
      const ob = oldById.get(d.id);
      if (ob) out.push({ ...ob, revision: { kind: 'removed' } });
      continue;
    }
    const nb = newById.get(d.id);
    if (!nb) continue;
    if (d.kind === 'unchanged') out.push(nb);
    else out.push({ ...nb, revision: { kind: d.kind, wordDiff: d.wordDiff } });
  }
  return out;
}

/* ═══════════════════════════════════════════════════════════════
   DIFF UTILITY — Compare two sets of blocks
   ═══════════════════════════════════════════════════════════════ */
export function diffBlocks(oldBlocks: ContentBlock[], newBlocks: ContentBlock[]): BlockDiff[] {
  const oldMap = new Map(oldBlocks.map((b) => [b.id, b]));
  const newMap = new Map(newBlocks.map((b) => [b.id, b]));
  const diffs: BlockDiff[] = [];

  // Walk new blocks in order
  for (const nb of newBlocks) {
    const ob = oldMap.get(nb.id);
    if (!ob) {
      diffs.push({
        blockId: nb.id,
        level: nb.level,
        type: 'added',
        newText: nb.text,
        newLevel: nb.level,
      });
    } else if (ob.text !== nb.text || ob.level !== nb.level) {
      diffs.push({
        blockId: nb.id,
        level: nb.level,
        type: 'modified',
        oldText: ob.text,
        newText: nb.text,
        oldLevel: ob.level,
        newLevel: nb.level,
      });
    } else {
      diffs.push({
        blockId: nb.id,
        level: nb.level,
        type: 'unchanged',
        oldText: ob.text,
        newText: nb.text,
      });
    }
  }

  // Find removed blocks (in old but not in new)
  for (const ob of oldBlocks) {
    if (!newMap.has(ob.id)) {
      diffs.push({
        blockId: ob.id,
        level: ob.level,
        type: 'removed',
        oldText: ob.text,
        oldLevel: ob.level,
      });
    }
  }

  return diffs;
}

/* ═══════════════════════════════════════════════════════════════
   CHANGE SUMMARY — Systems, Block Groups, Variants comparison
   ═══════════════════════════════════════════════════════════════ */

export interface ChangeSummary {
  systemsAdded: string[];
  systemsRemoved: string[];
  systemsModified: string[];
  variantsAdded: string[];
  variantsRemoved: string[];
  variantsModified: string[];
}

export function diffChangeSummary(
  master: { systems: SystemModule[]; variants?: Variant[] },
  draft: { systems: SystemModule[]; variants?: Variant[] }
): ChangeSummary {
  const summary: ChangeSummary = {
    systemsAdded: [],
    systemsRemoved: [],
    systemsModified: [],
    variantsAdded: [],
    variantsRemoved: [],
    variantsModified: [],
  };

  // Systems
  const oldSys = new Map((master.systems || []).map((s) => [s.id, s]));
  const newSys = new Map((draft.systems || []).map((s) => [s.id, s]));
  for (const [id, ns] of newSys) {
    const os = oldSys.get(id);
    if (!os) {
      summary.systemsAdded.push(ns.name);
    } else if (os.name !== ns.name || JSON.stringify(os.content) !== JSON.stringify(ns.content)) {
      summary.systemsModified.push(ns.name);
    }
  }
  for (const [id, os] of oldSys) {
    if (!newSys.has(id)) summary.systemsRemoved.push(os.name);
  }

  // Variants
  const oldVar = new Map((master.variants || []).map((v) => [v.id, v]));
  const newVar = new Map((draft.variants || []).map((v) => [v.id, v]));
  for (const [id, nv] of newVar) {
    const ov = oldVar.get(id);
    if (!ov) {
      summary.variantsAdded.push(nv.name);
    } else if (
      ov.name !== nv.name ||
      ov.status !== nv.status ||
      ov.description !== nv.description ||
      JSON.stringify(ov.tags) !== JSON.stringify(nv.tags)
    ) {
      summary.variantsModified.push(nv.name);
    }
  }
  for (const [id, ov] of oldVar) {
    if (!newVar.has(id)) summary.variantsRemoved.push(ov.name);
  }

  return summary;
}

/** No two master specs in a firm share a section number (D5,
 *  TABLE_BORDERS_AND_RENAME_PLAN.md T2.2). Case-insensitive, trims
 *  whitespace. Archived masters count — they can be restored. Titles are
 *  NEVER checked. Every path that sets a number (rename, New, Save As)
 *  calls this one helper so the rule can't drift between surfaces. */
export function csiConflict(
  masters: SpecMaster[],
  csiNumber: string,
  excludeId?: string
): SpecMaster | null {
  const k = (csiNumber || '').trim().toLowerCase();
  if (!k) return null;
  return (
    masters.find((m) => (m.csiNumber || '').trim().toLowerCase() === k && m.id !== excludeId) ||
    null
  );
}

/** Next free duplicate number for a section: the source number kept verbatim
 *  plus `.01`; if that is taken, `.02`, `.03`… (SPEC_WORKFLOW §11). Works for
 *  any list of numbers — masters in the library, specs already in a project. */
export function nextDuplicateNumber(
  taken: ReadonlyArray<{ csiNumber?: string }>,
  csiNumber: string
): string {
  // Duplicating `27 41 30.01` yields the next free `27 41 30.NN`, never `.01.01`.
  const base = (csiNumber || '').trim().replace(/\.\d{2}$/, '');
  const squash = (v: string) => v.replace(/\s/g, '').toLowerCase();
  const used = new Set(taken.map((m) => squash(m.csiNumber || '')));
  for (let n = 1; n < 1000; n++) {
    const candidate = `${base}.${String(n).padStart(2, '0')}`;
    if (!used.has(squash(candidate))) return candidate;
  }
  return `${base}.${Date.now()}`;
}

/** cloudCache.save() wraps every failure as `Failed to save to <table>: <msg>`
 *  so a server-side 409 body message ("That section number is already in
 *  use") still arrives at the caller — just with that prefix. Strip it so
 *  the toast shows the server's message verbatim, never the wrapper. */
export function cleanSaveError(err: unknown): string {
  const msg = err instanceof Error ? err.message : 'Unknown error';
  return msg.replace(/^Failed to (save to|delete from) \w+:\s*/, '');
}

/* ═══════════════════════════════════════════════════════════════
   SERVICE OBJECT — All CRUD operations delegate to window.api
   ═══════════════════════════════════════════════════════════════ */
export const SpecService = {
  /* ── Utility ── */
  generateId: (): string =>
    'id-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 6),
  generateProjectId: (): string =>
    'proj-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 4),

  /* ── Reference Data ── */
  getDivisions: (): CsiDivision[] => CSI_DIVISIONS,
  getSystemCatalog: (): SystemCatalogEntry[] => SYSTEM_CATALOG,

  /* ══════════════════════════════════════════════════════════════
     SPEC MASTERS (Published — the "main branch")
     ══════════════════════════════════════════════════════════════ */
  listMasters: async (): Promise<SpecMaster[]> => SpecMasterService.list(),

  getMaster: async (id: string): Promise<SpecMaster | null> => SpecMasterService.get(id),

  /** The next master / draft read goes to the server, not the cache. */
  forgetCachedMasters: (): void => SpecMasterService.forgetCached(),

  /** Direct save to published spec master (used internally for merge/admin operations) */
  savePublished: async (master: SpecMaster): Promise<SpecMaster> => {
    master.lastModified = new Date().toISOString();
    return SpecMasterService.save(master);
  },

  deleteMaster: async (id: string): Promise<void> => SpecMasterService.delete(id),

  /** Save As — duplicate with new ID, reset version to 1, clear history */
  saveAsNewMaster: async (
    source: SpecMaster,
    newTitle: string,
    userName?: string
  ): Promise<SpecMaster> => {
    const newMaster: SpecMaster = {
      ...JSON.parse(JSON.stringify(source)),
      id: SpecService.generateId(),
      title: newTitle,
      version: 1,
      status: 'draft' as SpecMasterStatus,
      versionHistory: [],
      lockedBy: undefined,
      lockedAt: undefined,
      lastModified: new Date().toISOString(),
      modifiedBy: userName || source.modifiedBy,
      editHistory: pushEditHistory(
        [],
        userName || source.modifiedBy || 'Unknown',
        undefined,
        'created'
      ),
    };
    return SpecMasterService.save(newMaster);
  },

  /* ══════════════════════════════════════════════════════════════
     DRAFTS (Working copies — "feature branches")
     ══════════════════════════════════════════════════════════════ */
  listDrafts: async (): Promise<SpecMasterDraft[]> => SpecMasterService.listDrafts(),

  getDraft: async (id: string): Promise<SpecMasterDraft | null> => SpecMasterService.getDraft(id),

  /** The ONE shared draft of a master (live shared drafts,
   *  docs/SPEC_WORKFLOW.md §5) — whoever started it. The newest row wins when
   *  history left more than one (pre-collab per-user drafts). */
  getSharedDraft: async (blueprintId: string): Promise<SpecMasterDraft | null> => {
    const list = await SpecMasterService.listDrafts();
    const mine = list.filter((d) => d.blueprintId === blueprintId);
    if (!mine.length) return null;
    return [...mine].sort((a, b) => (b.lastSaved || '').localeCompare(a.lastSaved || ''))[0];
  },

  /** A fresh shared-draft record for a master — the draft checkout used to
   *  create, minus the lock (a shared draft is never locked). `id` is the
   *  deterministic shared id, so the collab server's first store and this
   *  record land on the same row. Not persisted here. */
  newSharedDraft: (master: SpecMaster, id: string, userName: string): SpecMasterDraft => ({
    id,
    blueprintId: master.id,
    baseVersion: master.version,
    editedBy: userName,
    csiNumber: master.csiNumber,
    title: master.title,
    description: master.description,
    coreContent: JSON.parse(JSON.stringify(master.coreContent || [])),
    systems: JSON.parse(JSON.stringify(master.systems || [])),
    variants: JSON.parse(JSON.stringify(master.variants || [])),
    layers: JSON.parse(JSON.stringify(master.layers || [])),
    createdAt: new Date().toISOString(),
    lastSaved: new Date().toISOString(),
  }),

  /** SAVE DRAFT — Save to cloud, doesn't touch the published spec master */
  saveDraft: async (
    draft: SpecMasterDraft,
    o?: { collabMirror?: boolean }
  ): Promise<SpecMasterDraft> => {
    draft.lastSaved = new Date().toISOString();
    return SpecMasterService.saveDraft(draft, o);
  },

  /** DISCARD DRAFT — Delete the draft and unlock the spec master */
  discardDraft: async (draftId: string): Promise<void> => {
    const draft = await SpecService.getDraft(draftId);
    if (!draft) return;

    // Unlock the spec master
    const master = await SpecService.getMaster(draft.blueprintId);
    if (master && master.lockedBy === draft.editedBy) {
      master.lockedBy = undefined;
      master.lockedAt = undefined;
      await SpecService.savePublished(master);
    }

    await SpecMasterService.deleteDraft(draftId);
  },

  /** FORCE OVERRIDE — Discard another user's draft and unlock the spec master */
  forceOverrideDraft: async (masterId: string): Promise<void> => {
    // Find and delete any draft for this master
    const allDrafts = await SpecService.listDrafts();
    const draftsForMaster = allDrafts.filter((d) => d.blueprintId === masterId);
    for (const d of draftsForMaster) {
      await SpecMasterService.deleteDraft(d.id);
    }

    // Force-unlock the spec master
    const master = await SpecService.getMaster(masterId);
    if (master) {
      master.lockedBy = undefined;
      master.lockedAt = undefined;
      await SpecService.savePublished(master);
    }
  },

  /**
   * SUBMIT PUBLISH REQUEST — Like opening a PR.
   * Generates a diff and attaches a PublishRequest to the draft.
   */
  submitPublishRequest: async (
    draftId: string,
    message?: string
  ): Promise<SpecMasterDraft | null> => {
    const draft = await SpecService.getDraft(draftId);
    if (!draft) return null;

    const master = await SpecService.getMaster(draft.blueprintId);
    if (!master) return null;

    draft.publishRequest = {
      id: SpecService.generateId(),
      draftId: draft.id,
      blueprintId: draft.blueprintId,
      requestedBy: draft.editedBy,
      requestedAt: new Date().toISOString(),
      message,
      status: 'pending',
    };
    draft.lastSaved = new Date().toISOString();

    await SpecMasterService.saveDraft(draft);
    return draft;
  },

  /* PUBLISH lives in ONE place: `commitPublish` below (SPEC_WORKFLOW §7).
     The old merge (`publishDraft`) and its pending-offer raise are gone — a
     review-queue Approve opens the Studio's publish dialog instead. */

  /** REJECT PUBLISH — Admin rejects the PR. Draft stays for editing. */
  rejectPublish: async (
    draftId: string,
    reviewerName: string,
    note?: string
  ): Promise<SpecMasterDraft | null> => {
    const draft = await SpecService.getDraft(draftId);
    if (!draft || !draft.publishRequest) return null;

    draft.publishRequest.status = 'rejected';
    draft.publishRequest.reviewedBy = reviewerName;
    draft.publishRequest.reviewedAt = new Date().toISOString();
    draft.publishRequest.reviewNote = note;
    draft.lastSaved = new Date().toISOString();

    await SpecMasterService.saveDraft(draft);
    return draft;
  },

  /** Every draft row for a master — the shared one and any older per-user
   *  drafts, so Discard and Publish leave none behind to resurface. */
  draftIdsFor: async (masterId: string): Promise<string[]> =>
    SpecMasterService.draftIdsFor(masterId),

  /**
   * STRICT PUBLISH (docs/SPEC_WORKFLOW.md §7) — the ONE write path of a
   * Studio publish: the new master, the variants' answered feed rows and the
   * removal of every draft row, in ONE server transaction. A failure throws
   * the server's error (`failedOp` set) and nothing has changed.
   */
  commitPublish: async (result: PublishResult, draftIds: string[]): Promise<SpecMaster> => {
    await cache.batch(
      publishWrites(result, draftIds, {
        saveMaster: SpecMasterService.saveWrite,
        createEntries: VariantChangeService.createWrite,
        removeDraft: SpecMasterService.removeDraftWrite,
      })
    );
    logActivity({
      action: 'spec.update',
      entityType: 'spec_master',
      entityId: result.master.id,
      metadata: { title: result.master.title, csiNumber: result.master.csiNumber },
    });
    return result.master;
  },

  /** Revert to a previous version (creates a new version) */
  revertMaster: async (
    id: string,
    targetVersion: number,
    userName?: string
  ): Promise<SpecMaster | null> => {
    const master = await SpecService.getMaster(id);
    if (!master || !master.versionHistory) return null;
    const versionEntry = master.versionHistory.find((v) => v.version === targetVersion);
    if (!versionEntry) return null;

    // Archive current state
    const maxVersions = master.maxVersionsKept || 10;
    const snapshot: SpecMasterVersion = {
      version: master.version,
      snapshot: {
        coreContent: JSON.parse(JSON.stringify(master.coreContent)),
        systems: JSON.parse(JSON.stringify(master.systems)),
        title: master.title,
        description: master.description,
      },
      savedBy: userName || master.modifiedBy || 'unknown',
      savedAt: master.lastModified,
      message: `Reverted to v${targetVersion}`,
    };
    const history = [...(master.versionHistory || []), snapshot];
    master.versionHistory = history.slice(-maxVersions);

    // Restore the target version
    master.coreContent = JSON.parse(JSON.stringify(versionEntry.snapshot.coreContent));
    master.systems = JSON.parse(JSON.stringify(versionEntry.snapshot.systems));
    if (versionEntry.snapshot.title) master.title = versionEntry.snapshot.title;
    if (versionEntry.snapshot.description) master.description = versionEntry.snapshot.description;
    master.version = (master.version || 0) + 1;
    master.modifiedBy = userName;
    master.lastModified = new Date().toISOString();

    return await SpecService.savePublished(master);
  },

  /** Get diff between a draft and its published blueprint */
  getDraftDiff: async (draftId: string): Promise<BlockDiff[]> => {
    const draft = await SpecService.getDraft(draftId);
    if (!draft) return [];
    const master = await SpecService.getMaster(draft.blueprintId);
    if (!master) return [];
    return diffBlocks(master.coreContent || [], draft.coreContent || []);
  },

  /* ══ Projects CRUD (delegates to ProjectService) ══ */
  listProjects: async (): Promise<Project[]> => ProjectService.list(),
  getProject: async (id: string): Promise<Project | null> => {
    const all = await ProjectService.list();
    return all.find((p) => p.id === id) || null;
  },
  saveProject: async (project: Project): Promise<Project> => ProjectService.save(project),
  deleteProject: async (id: string): Promise<void> => ProjectService.delete(id),

  /* ══ Spec Assembly (for Forge/Build components) ══ */
  assembleSpec: async (projectId: string, masterId: string): Promise<AssembledSpec | null> => {
    const project = await SpecService.getProject(projectId);
    const master = await SpecService.getMaster(masterId);
    if (!project || !master) return null;
    const sel = project.selections[masterId] || { enabledSystems: [], options: {}, parameters: {} };
    let blocks = [
      ...(master.coreContent || []).map((b) => ({ ...b, source: 'core', included: true })),
    ];
    (master.systems || []).forEach((sys) => {
      const enabled = sel.enabledSystems.includes(sys.id);
      (sys.content || []).forEach((b) => {
        blocks.push({
          ...b,
          source: 'system',
          systemName: sys.name,
          systemId: sys.id,
          included: enabled,
        });
      });
    });
    return { master, blocks, selections: sel };
  },
};

/* ── Expose for legacy browser globals ── */
// Guarded: the pure diff functions in this module are also imported by
// node-side unit tests, where no window exists.
if (typeof window !== 'undefined') {
  (window as any).SpecService = SpecService;
  (window as any).CSI_DIVISIONS = CSI_DIVISIONS;
  (window as any).SYSTEM_CATALOG = SYSTEM_CATALOG;
}
